/**
 * Keeping circles apart in the tree.
 *
 * The layout (layout.ts) gives every person a resting spot with just enough
 * room for a circle at its normal size. A circle that has grown — fresh
 * activity, a "you" or star ring — would overlap its neighbours, so this
 * module nudges the row apart by exactly the overlap and no more: each circle
 * moves as little as possible, rows stay straight (only x changes), and when
 * the circle settles back down its neighbours return to their resting spots.
 *
 * Pure functions here; the smooth motion between two such solutions is a small
 * spring in `useSettledPositions` (TreeScreen).
 */
import type { UUID } from '../../domain/types';

export interface RowNode {
  id: UUID;
  /** Resting spot from the layout. */
  x: number;
  y: number;
  /** Half of what the circle really takes up on screen right now. */
  radius: number;
}

/**
 * Minimal-displacement spacing for one row. With the required spacing between
 * neighbours turned into a cumulative offset, "no overlap" becomes "positions
 * never decrease", and the least-squares answer to that is isotonic regression
 * (pool-adjacent-violators): overlapping neighbours merge into a block placed
 * at the average of their resting spots. Exact, deterministic, O(n log n).
 */
export function relaxRow(nodes: RowNode[], gap: number): Map<UUID, number> {
  const out = new Map<UUID, number>();
  if (!nodes.length) return out;
  const sorted = [...nodes].sort((a, b) => a.x - b.x || (a.id < b.id ? -1 : 1));
  // c[i]: where node i would sit if the whole row were packed shoulder to shoulder from c[0] = 0.
  const c: number[] = [0];
  for (let i = 1; i < sorted.length; i++) c.push(c[i - 1] + sorted[i - 1].radius + sorted[i].radius + gap);
  // Targets in the packed frame; a valid answer is non-decreasing in this frame.
  const t = sorted.map((n, i) => n.x - c[i]);
  // PAV
  const blocks: Array<{ sum: number; n: number; start: number }> = [];
  for (let i = 0; i < t.length; i++) {
    let b = { sum: t[i], n: 1, start: i };
    while (blocks.length && blocks[blocks.length - 1].sum / blocks[blocks.length - 1].n > b.sum / b.n) {
      const prev = blocks.pop()!;
      b = { sum: prev.sum + b.sum, n: prev.n + b.n, start: prev.start };
    }
    blocks.push(b);
  }
  let i = 0;
  for (const b of blocks) {
    const q = b.sum / b.n;
    for (let k = 0; k < b.n; k++, i++) out.set(sorted[i].id, q + c[i]);
  }
  return out;
}

/** Every row of the tree at once (rows = equal y). Returns the x each circle should sit at. */
export function relaxRows(nodes: RowNode[], gap: number): Map<UUID, number> {
  const rows = new Map<number, RowNode[]>();
  for (const n of nodes) {
    const key = Math.round(n.y);
    const row = rows.get(key);
    if (row) row.push(n);
    else rows.set(key, [n]);
  }
  const out = new Map<UUID, number>();
  for (const row of rows.values()) for (const [id, x] of relaxRow(row, gap)) out.set(id, x);
  return out;
}

export interface BandNode {
  id: UUID;
  /** Resting x. */
  x: number;
  /** Node-box centre y (only used to group; the caller sets `top`/`bottom`). */
  y: number;
  /** Half of what the node takes up sideways right now. */
  halfW: number;
  /** Vertical extent of what is drawn: top of the circle to the bottom of the name. */
  top: number;
  bottom: number;
}

/**
 * Sideways-only spacing for the "by age" arrangement, where people sit at
 * arbitrary heights. Two nodes whose vertical extents overlap must be at least
 * `halfW + halfW + gap` apart horizontally; heights are never touched (they
 * are the information). Overlapping pairs are pushed apart symmetrically, in
 * their original left-to-right order, until nothing overlaps — a chain of
 * overlaps propagates, so a couple pushed on one side moves together.
 * Deterministic; O(pairs × iterations).
 *
 * Only neighbours are paired. Two people who rest more than `BAND_REACH` apart
 * are never going to be pushed into each other — a push moves a circle by a
 * fraction of its own width — and leaving them out is what keeps a family of a
 * thousand at a few tens of thousands of pairs rather than half a million,
 * every one of them walked eighty times.
 */
const BAND_REACH = 1200;

export function relaxBands(nodes: BandNode[], gap: number, maxIterations = 80): Map<UUID, number> {
  const sorted = [...nodes].sort((a, b) => a.x - b.x || (a.id < b.id ? -1 : 1));
  const x = sorted.map((n) => n.x);
  const pairs: Array<[number, number, number]> = [];
  for (let i = 0; i < sorted.length; i++) {
    const a = sorted[i];
    for (let j = i + 1; j < sorted.length; j++) {
      const b = sorted[j];
      // Sorted by x, so everyone after this one is further away still.
      if (b.x - a.x > BAND_REACH) break;
      if (a.top < b.bottom && b.top < a.bottom) pairs.push([i, j, a.halfW + b.halfW + gap]);
    }
  }
  for (let it = 0; it < maxIterations && pairs.length; it++) {
    let worst = 0;
    for (const [i, j, need] of pairs) {
      const dx = x[j] - x[i];
      if (dx < need) {
        const shift = (need - dx) / 2;
        x[i] -= shift;
        x[j] += shift;
        if (shift > worst) worst = shift;
      }
    }
    if (worst < 0.05) break;
  }
  const out = new Map<UUID, number>();
  sorted.forEach((n, i) => out.set(n.id, x[i]));
  return out;
}

/**
 * One step of a damped spring from `x` towards `target`. Slightly under-damped,
 * so a circle that is let go eases back with the faintest overshoot rather
 * than sliding to a dead stop. Returns the new position and velocity.
 */
export function springStep(x: number, v: number, target: number, dtMs: number, stiffness = 120, damping = 17): { x: number; v: number } {
  // Sub-step for stability when a frame is long (tab was hidden, a slow phone).
  const steps = Math.max(1, Math.ceil(dtMs / 8));
  const h = dtMs / steps / 1000;
  let px = x;
  let pv = v;
  for (let i = 0; i < steps; i++) {
    const a = stiffness * (target - px) - damping * pv;
    pv += a * h;
    px += pv * h;
  }
  return { x: px, v: pv };
}
